import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { Card } from '@/components/layout';
import { MainSection } from '@/components/layout/MainSection';
import AdminToolbar from '@/components/AdminToolbar/index';
import { isCompleted } from '@/constants/matchStatus';
import { useTranslation } from 'react-i18next';

const DebugApiTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { isAuthenticated } = useAuth();

  const [leagueCode, setLeagueCode] = useState('PL');
  const [showRawData, setShowRawData] = useState(false);

  const {
    data: apiData,
    isLoading: apiLoading,
    error: apiError,
    refetch: refetchApiData
  } = useQuery({
    queryKey: ['debug-football-api', leagueCode],
    queryFn: async () => {
      const response = await apiClient.get(`/admin/matches/debug-api/${leagueCode}`);
      return response.data.data;
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: false
  });

  const debugCompetitions = [
    { value: 'PL', label: 'Premier League' },
    { value: 'CL', label: 'Champions League' }
  ];

  return (
    <MainSection title={t('admin:debugApi')}>

      {/* Admin Toolbar */}
      <AdminToolbar />

      {/* Controls */}
      <Card className="ls-mb-4">
        <div className="ls-flex ls-align-center ls-gap-3 ls-flex-wrap">
          <div className="ls-field ls-flex-1" style={{ minWidth: '150px' }}>
            <label className="ls-label ls-mb-1">Выберите лигу:</label>
            <select value={leagueCode} onChange={(e) => setLeagueCode(e.target.value)} className="ls-input">
              {debugCompetitions.map((comp) => (
                <option key={comp.value} value={comp.value}>{comp.label} ({comp.value})</option>
              ))}
            </select>
          </div>

          <div className="ls-flex ls-gap-2" style={{ paddingTop: '20px' }}>
            <button onClick={() => refetchApiData()} disabled={apiLoading} className="ls-btn" style={{ fontSize: '13px', opacity: apiLoading ? 0.5 : 1 }}>
              {apiLoading ? 'Загрузка...' : '🔄 Обновить'}
            </button>
            <button onClick={() => setShowRawData(!showRawData)} className="ls-btn ls-btn--outlined" style={{ fontSize: '13px' }}>
              {showRawData ? 'Скрыть Raw JSON' : 'Показать Raw JSON'}
            </button>
          </div>
        </div>
      </Card>

      {/* Loading State */}
      {apiLoading && (
        <Card className="ls-text-center">
          <div className="ls-flex ls-justify-center ls-align-center ls-py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
          </div>
          <p className="theme-text-secondary">Загрузка данных из API-Football...</p>
        </Card>
      )}

      {/* Error State */}
      {apiError && (
        <Card className="theme-bg-error ls-mb-4">
          <h3 className="ls-label theme-text-error ls-mb-2">❌ Ошибка API</h3>
          <p className="theme-text-error" style={{ fontSize: '13px' }}>{(apiError as any).message || 'Не удалось загрузить данные'}</p>
        </Card>
      )}

      {/* API Data */}
      {apiData && (
        <>
          {/* Summary */}
          <Card className="ls-mb-4">
            <h2 className="ls-h4 ls-mb-3">📊 Сводка данных</h2>
            <div className="ls-grid ls-col-2 ls-gap-3">
              <div className="ls-text-center ls-py-2 theme-bg-tertiary" style={{ borderRadius: '8px' }}>
                <div className="ls-h3 theme-text-link">{apiData.apiMatches?.length || 0}</div>
                <div className="theme-text-muted" style={{ fontSize: '11px' }}>Всего матчей</div>
              </div>
              <div className="ls-text-center ls-py-2 theme-bg-tertiary" style={{ borderRadius: '8px' }}>
                <div className="ls-h3 theme-status-success">
                  {apiData.apiMatches?.filter((m: any) => isCompleted(m.status)).length || 0}
                </div>
                <div className="theme-text-muted" style={{ fontSize: '11px' }}>Завершенных</div>
              </div>
              <div className="ls-text-center ls-py-2 theme-bg-tertiary" style={{ borderRadius: '8px' }}>
                <div className="ls-h3" style={{ color: 'var(--accent-primary)' }}>
                  {apiData.apiMatches?.filter((m: any) => isCompleted(m.status) && m.score?.homeTeam !== null).length || 0}
                </div>
                <div className="theme-text-muted" style={{ fontSize: '11px' }}>Со счетом</div>
              </div>
              <div className="ls-text-center ls-py-2 theme-bg-tertiary" style={{ borderRadius: '8px' }}>
                <div className="ls-h3 theme-text-link">{apiData.dbMatches?.length || 0}</div>
                <div className="theme-text-muted" style={{ fontSize: '11px' }}>В БД</div>
              </div>
            </div>

            {apiData.apiKey && (
              <div className="ls-mt-3 ls-py-2 ls-px-3 theme-bg-status-success" style={{ borderRadius: '8px' }}>
                <span className="theme-status-success" style={{ fontSize: '12px' }}>✅ API ключ настроен: {apiData.apiKey.substring(0, 8)}...</span>
              </div>
            )}

            {!apiData.apiKey && (
              <div className="ls-mt-3 ls-py-2 ls-px-3" style={{ borderRadius: '8px', background: 'var(--bg-status-warning, rgba(255, 193, 7, 0.1))' }}>
                <span className="theme-status-warning" style={{ fontSize: '12px' }}>⚠️ API ключ отсутствует - используются mock данные</span>
              </div>
            )}
          </Card>

          {/* Matches Analysis */}
          <Card className="ls-mb-4">
            <h2 className="ls-h4 ls-mb-3">⚽ Анализ матчей</h2>

            {apiData.apiMatches && apiData.apiMatches.length > 0 ? (
              <div className="ls-flex ls-flex-col ls-gap-2">
                {apiData.apiMatches.slice(0, 20).map((match: any, index: number) => (
                  <div key={index} className="theme-bg-tertiary ls-py-3 ls-px-3" style={{ borderRadius: '8px' }}>
                    <div className="ls-flex ls-align-center ls-justify-between ls-mb-2">
                      <div className="ls-flex ls-align-center ls-gap-2">
                        <span className="ls-label" style={{ fontSize: '13px' }}>
                          {match.homeTeam} vs {match.awayTeam}
                        </span>
                        <span className={`ls-tag ${isCompleted(match.status) ? 'ls-tag--green' : 'ls-tag--blue'}`}>
                          {match.status}
                        </span>
                      </div>
                      <span className="theme-text-muted" style={{ fontSize: '11px' }}>
                        {new Date(match.matchDate).toLocaleDateString('ru-RU')}
                      </span>
                    </div>

                    <div className="ls-flex ls-gap-3 theme-text-muted" style={{ fontSize: '11px' }}>
                      <span>ID: {match.externalMatchId}</span>
                      <span>Тур: {match.matchday || 'N/A'}</span>
                      <span>Стадия: {match.stage || 'N/A'}</span>
                    </div>

                    {isCompleted(match.status) && (
                      <div className="ls-mt-2 ls-py-2 ls-px-2" style={{ borderRadius: '4px', background: 'var(--bg-card)' }}>
                        <div className="ls-label" style={{ fontSize: '12px' }}>
                          Счет: {match.score?.homeTeam !== null && match.score?.awayTeam !== null
                            ? `${match.score.homeTeam} : ${match.score.awayTeam}`
                            : '❌ НЕТ СЧЕТА'}
                        </div>
                        {match.score?.homeTeam !== null && (
                          <div className="theme-text-muted" style={{ fontSize: '10px' }}>
                            Raw: {JSON.stringify(match.score)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {apiData.apiMatches.length > 20 && (
                  <div className="ls-text-center theme-text-muted" style={{ fontSize: '12px' }}>
                    ... и еще {apiData.apiMatches.length - 20} матчей
                  </div>
                )}
              </div>
            ) : (
              <p className="theme-text-secondary ls-text-center ls-py-4">Матчи не найдены</p>
            )}
          </Card>

          {/* Raw JSON Data */}
          {showRawData && (
            <Card>
              <h2 className="ls-h4 ls-mb-3">📄 Raw JSON Response</h2>
              <pre
                className="theme-bg-tertiary ls-py-3 ls-px-3"
                style={{ borderRadius: '8px', fontSize: '10px', overflow: 'auto', maxHeight: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
              >
                {JSON.stringify(apiData, null, 2)}
              </pre>
            </Card>
          )}
        </>
      )}
    </MainSection>
  );
};

export default DebugApiTab;
